import { createApp, h, ref, type App } from 'vue'
import CustomDialog from '@/components/CustomDialog.vue'

interface DialogOptions {
  title?: string
  description?: string
  icon?: string
  iconColor?: string
  tipText?: string
  tipType?: 'success' | 'warning' | 'error'
  showCancel?: boolean
  showConfirm?: boolean
  cancelText?: string
  confirmText?: string
  closeable?: boolean
  closeOnClickOverlay?: boolean
  footerLayout?: 'horizontal' | 'vertical'
}

interface DialogInstance {
  app: App
  container: HTMLDivElement
  close: () => void
}

let currentDialog: DialogInstance | null = null

export function showDialog(options: DialogOptions): Promise<boolean> {
  return new Promise((resolve, reject) => {
    // 如果已有弹窗，先关闭
    if (currentDialog) {
      currentDialog.close()
    }

    // 创建容器
    const container = document.createElement('div')
    document.body.appendChild(container)

    let resolved = false

    const close = () => {
      if (currentDialog && currentDialog.container === container) {
        currentDialog = null
      }
      app.unmount()
      document.body.removeChild(container)
    }

    // 创建 Vue 应用实例
    const app = createApp({
      setup() {
        const show = ref(true)

        const handleConfirm = () => {
          if (!resolved) {
            resolved = true
            resolve(true)
          }
          show.value = false
          setTimeout(close, 300) // 等待动画完成
        }

        const handleCancel = () => {
          if (!resolved) {
            resolved = true
            reject('cancel')
          }
          show.value = false
          setTimeout(close, 300)
        }

        const handleUpdateShow = (value: boolean) => {
          if (!value && !resolved) {
            resolved = true
            reject('cancel')
          }
        }

        return () =>
          h(CustomDialog, {
            show: show.value,
            'onUpdate:show': handleUpdateShow,
            onConfirm: handleConfirm,
            onCancel: handleCancel,
            ...options,
          })
      },
    })

    app.mount(container)

    currentDialog = { app, container, close }
  })
}

// 便捷方法：确认对话框
export function showConfirm(
  tipText: string,
  description: string,
  options?: Partial<DialogOptions>,
): Promise<boolean> {
  return showDialog({
    tipText,
    description,
    icon: 'info-o',
    iconColor: '#3b7cff',
    showCancel: true,
    showConfirm: true,
    cancelText: '取消',
    confirmText: '确定',
    ...options,
  })
}

// 便捷方法：警告对话框
export function showWarning(
  title: string,
  description: string,
  options?: Partial<DialogOptions>,
): Promise<boolean> {
  return showDialog({
    title,
    description,
    icon: 'warning-o',
    iconColor: '#ff976a',
    showCancel: true,
    showConfirm: true,
    cancelText: '取消',
    confirmText: '确定',
    ...options,
  })
}

// 便捷方法：提示对话框（仅确认按钮）
export function showAlert(
  title: string,
  description: string,
  options?: Partial<DialogOptions>,
): Promise<boolean> {
  return showDialog({
    title,
    description,
    icon: 'info-o',
    showCancel: false,
    showConfirm: true,
    confirmText: '知道了',
    ...options,
  })
}
